import React, { useEffect, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import { prettyFormatE164Number } from 'flow/utils/call-utils';
import { extractName } from 'flow/utils';

import useAppContext from 'flow/hooks/useAppContext';
import { makeGetContactDataForNumber } from 'flow/selectors/contact-selectors';
import { selectUserCountryCode } from 'flow/selectors/ExtensionSelectors';

import { lookupNumber } from 'flow/actions/contact-actions';
import { selectWaitingQueueItem } from 'flow/selectors/queue-selectors';
import SupervisorCallPopover from './SupervisorCallPopover';

interface Props {
    queueCallKey: string;
    position: number;
    queueName: string;
}

export const SupervisorQueueCallPopover = ({
    queueCallKey,
    position,
    queueName,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const ownCountryCode = useAppSelector(selectUserCountryCode);

    const queueItem = useAppSelector((state) =>
        selectWaitingQueueItem(state, queueCallKey),
    );

    const number =
        queueItem && queueItem.getIn(['telavoxOmniTicketData', 'sender'])
            ? queueItem.getIn(['telavoxOmniTicketData', 'sender'])
            : queueItem.getIn(['caller', 'number']);

    const timestamp = queueItem.get('joinTime');
    const isOmnichannelErrand = queueItem.get('isOmnichannelErrand');

    const getContact = useMemo(makeGetContactDataForNumber, []);
    //@ts-ignore
    const contact = useAppSelector((state) => getContact(state, number));

    useEffect(() => {
        if (number && !contact && !isOmnichannelErrand) {
            dispatch(lookupNumber(number));
        }
    }, [dispatch, number, contact, isOmnichannelErrand]);

    const getTitle = () => {
        return isOmnichannelErrand
            ? queueItem.get('omnichannelErrandUserFriendlyDescription')
            : prettyFormatE164Number(number, ownCountryCode);
    };

    const getSubtitle = () => {
        return isOmnichannelErrand
            ? queueItem.get('omnichannelErrandSender')
            : extractName(contact, i18next.t('Contact.Unknown'));
    };

    return (
        <SupervisorCallPopover
            position={position}
            subtitle={getSubtitle()}
            title={getTitle()}
            queueName={queueName}
            timestamp={timestamp}
            iconKey={'timer'}
            channelType={queueItem.getIn(['telavoxOmniTicketData', 'channel'])}
        />
    );
};
